import "./customer-feedback.style.scss";

import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import IconThumbDown from "@amzn/stencil-react-icons/icons/icon-thumb-down";
import IconThumbDownFill from "@amzn/stencil-react-icons/icons/icon-thumb-down-fill";
import IconThumbUp from "@amzn/stencil-react-icons/icons/icon-thumb-up";
import IconThumbUpFill from "@amzn/stencil-react-icons/icons/icon-thumb-up-fill";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import React, { FunctionComponent, useState } from "react";

export enum CFMModuleName {
  hardwareSearchResults = "hardwareSearchResults",
  hardwareProductDetails = "hardwareProductDetails",
}

export interface CustomerFeedbackProps {
  moduleName: CFMModuleName;
  alignment: CustomerFeedbackAlignment;
  position: CustomerFeedbackIconPosition;
  margin?: string;
}

export enum CustomerFeedbackAlignment {
  Left = 1,
  Centered,
  Right,
}

export enum CustomerFeedbackIconPosition {
  Left = "left",
  Bottom = "bottom",
}

export enum CustomerFeedbackMargins {
  Top = "top",
  None = "none",
}

export const CustomerFeedback: FunctionComponent<CustomerFeedbackProps> = ({
  moduleName,
  alignment,
  position,
  margin = CustomerFeedbackMargins.Top,
}) => {
  const [upvoted, setUpvoted] = useState(false);
  const [downvoted, setDownvoted] = useState(false);

  const onUpvoteClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setUpvoted(!upvoted);
    setDownvoted(false);
  };

  const onDownvoteClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setDownvoted(!downvoted);
    setUpvoted(false);
  };

  const getIconPositioning = (): React.CSSProperties => {
    if (position === CustomerFeedbackIconPosition.Bottom) {
      return { alignItems: "center", flexDirection: "column" };
    }
    return {};
  };

  const getRowTopMargin = (): string => {
    return margin === CustomerFeedbackMargins.Top ? "S400" : "0";
  };

  return (
    <Row width="100%" margin={[getRowTopMargin(), 0, 0, 0]}>
      {(alignment === CustomerFeedbackAlignment.Right || alignment === CustomerFeedbackAlignment.Centered) && (
        <Spacer flex="1" />
      )}
      <div className="cfm-main" style={getIconPositioning()}>
        <div>
          <Text
            className="cfm-text"
            fontSize="T100"
            fontWeight="regular"
            color="neutral70"
            margin={[0, "S200", 0, 0]}
            data-cy={`cfm-title-${moduleName}`}
          >
            {getString(`customerFeedback.${moduleName}`)}
          </Text>
        </div>
        <div>
          <Link
            href="#"
            margin={[0, "S200", 0, 0]}
            onClick={onUpvoteClick}
            className="thumbsup-icon"
            data-cy={`${moduleName}-thumbsup`}
          >
            {!upvoted && <IconThumbUp title={getString("customerFeedback.thumbsUpTitle")} color="neutral90" />}
            {upvoted && <IconThumbUpFill title={getString("customerFeedback.thumbsUpTitle")} color="primary80" />}
          </Link>
          <Link
            href="#"
            onClick={onDownvoteClick}
            className="thumbsup-icon"
            data-cy={`${moduleName}-thumbsdown`}
          >
            {!downvoted && <IconThumbDown title={getString("customerFeedback.thumbsDownTitle")} color="neutral90" />}
            {downvoted && (
              <IconThumbDownFill title={getString("customerFeedback.thumbsDownTitle")} color="primary80" />
            )}
          </Link>
        </div>
      </div>
      {alignment === CustomerFeedbackAlignment.Centered && <Spacer flex="1" />}
    </Row>
  );
};
